/**
 * Selection for a grid whose rows come from a MUI `dataSource` — the server owns the tree.
 *
 *   tick a PARENT   ──► every child the server matches, loaded or not, joins the selection
 *   tick a LEAF     ──► that row joins
 *   untick a child  ──► its parent unticks
 *   header ☑        ──► every leaf in the scope
 *   scope changes   ──► selection drops; responses still in flight are ignored
 *
 * `useGridSelection`'s paged mode cannot serve this: it recognises groups by
 * `isAutoGenerated`, and in server-side tree data a parent is a REAL row from the server.
 *
 * The COMMITTED selection (`enhancedGridState.selectedRows`, what bulk actions act on) is
 * published EMPTY while any resolution is in flight. The checkboxes tick at once, but a bulk
 * action fired in that window must not act on the pre-click selection while the grid claims more.
 */
import type {
  DataGridPremiumProps,
  GridRowId,
  GridRowSelectionModel,
  GridValidRowModel,
} from '@mui/x-data-grid-premium';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { BaseGridStateWithHelpers } from '../shared/baseGridTypes';

type GridSelectionModelChange = NonNullable<DataGridPremiumProps['onRowSelectionModelChange']>;

export type DataSourceChildRowsResolver = (parent: GridValidRowModel) => Promise<readonly GridValidRowModel[]>;
export type DataSourceAllRowsResolver = () => Promise<readonly GridValidRowModel[]>;

export type DataSourceSelectionOptions = {
  enableSelection: boolean;
  enhancedGridState: BaseGridStateWithHelpers;
  apiRef?: DataGridPremiumProps['apiRef'];
  getRowId?: DataGridPremiumProps['getRowId'];
  /** A row whose selection means "all of its server children". */
  isParentRow?: (row: GridValidRowModel) => boolean;
  /** Every child of that parent in the current scope, including children never loaded. */
  resolveChildRows?: DataSourceChildRowsResolver;
  /** Every leaf row in the current scope, for the header checkbox. */
  resolveAllRows?: DataSourceAllRowsResolver;
  /**
   * The parent a leaf belongs to. Select-all resolves LEAVES; without this link, unticking a parent
   * afterwards cannot find its children, and every one of them would stay selected.
   */
  getParentRowId?: (row: GridValidRowModel) => GridRowId | undefined;
  /** Changing it drops the selection and ignores responses still in flight. */
  selectionScopeKey?: string;
};

const include = (ids: Iterable<GridRowId>): GridRowSelectionModel => ({ type: 'include', ids: new Set(ids) });

type SelectionState = {
  model: Set<GridRowId>;
  committed: Record<string, GridValidRowModel>;
  /** Leaves the user ticked themselves. They survive a scope change; resolved rows do not. */
  manual: Set<GridRowId>;
  /** Every child a ticked parent resolved to. */
  members: Map<GridRowId, Set<GridRowId>>;
  /** Every leaf the header checkbox resolved to. */
  allMembers: Set<GridRowId>;
};

const emptyState = (): SelectionState => ({
  model: new Set(),
  committed: {},
  manual: new Set(),
  members: new Map(),
  allMembers: new Set(),
});

const copyState = (state: SelectionState): SelectionState => ({
  model: new Set(state.model),
  committed: { ...state.committed },
  manual: new Set(state.manual),
  members: new Map([...state.members].map(([id, ids]) => [id, new Set(ids)])),
  allMembers: new Set(state.allMembers),
});

export function useDataSourceSelection({
  enableSelection,
  enhancedGridState,
  apiRef,
  getRowId,
  isParentRow,
  resolveChildRows,
  resolveAllRows,
  getParentRowId,
  selectionScopeKey,
}: DataSourceSelectionOptions) {
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(() => include([]));
  const stateRef = useRef<SelectionState>(emptyState());
  const pendingRef = useRef(new Map<GridRowId | 'all', number>());
  /** The selection as it was before a header walk that is still in flight. */
  const headerSnapshotRef = useRef<SelectionState>();
  const sequenceRef = useRef(0);
  const headerIntentRef = useRef<boolean>();
  const scopeRef = useRef(selectionScopeKey);

  const idOf = useCallback((row: GridValidRowModel): GridRowId => getRowId?.(row) ?? row.id, [getRowId]);

  const publish = useCallback(() => {
    setSelectionModel(include(stateRef.current.model));
    enhancedGridState.updateSelectedRows(pendingRef.current.size > 0 ? {} : { ...stateRef.current.committed });
  }, [enhancedGridState]);

  const cancelPending = useCallback(() => {
    pendingRef.current = new Map();
    headerSnapshotRef.current = undefined;
    sequenceRef.current += 1;
  }, []);

  // A new scope drops what was RESOLVED under the old one. Leaves the user ticked themselves stay.
  useLayoutEffect(() => {
    if (scopeRef.current === selectionScopeKey) return;
    scopeRef.current = selectionScopeKey;
    cancelPending();
    const { manual, committed } = stateRef.current;
    const kept = emptyState();
    for (const id of manual) {
      kept.model.add(id);
      kept.manual.add(id);
      const row = committed[String(id)];
      if (row) kept.committed[String(id)] = row;
    }
    stateRef.current = kept;
    publish();
  }, [cancelPending, publish, selectionScopeKey]);

  useEffect(() => {
    const api = apiRef?.current;
    if (!api) return;
    return api.subscribeEvent(
      'headerSelectionCheckboxChange',
      ({ value }) => {
        headerIntentRef.current = value;
        queueMicrotask(() => {
          headerIntentRef.current = undefined;
        });
      },
      { isFirst: true }
    );
  }, [apiRef]);

  /** Runs one resolution; its result lands only if nothing superseded it. */
  const resolve = useCallback(
    (
      key: GridRowId | 'all',
      request: () => Promise<readonly GridValidRowModel[]>,
      onRows: (rows: readonly GridValidRowModel[]) => void,
      onError: () => void
    ) => {
      const token = ++sequenceRef.current;
      const scope = scopeRef.current;
      pendingRef.current.set(key, token);
      void request()
        .then((rows) => {
          if (pendingRef.current.get(key) !== token || scopeRef.current !== scope) return;
          pendingRef.current.delete(key);
          onRows(rows);
          publish();
        })
        .catch(() => {
          if (pendingRef.current.get(key) !== token) return;
          pendingRef.current.delete(key);
          onError();
          publish();
        });
    },
    [publish]
  );

  const onRowSelectionModelChange = useCallback<GridSelectionModelChange>(
    (incoming) => {
      let incomingIds = incoming.type === 'include' ? new Set(incoming.ids) : new Set<GridRowId>();
      const headerIntent = headerIntentRef.current;
      headerIntentRef.current = undefined;

      if (headerIntent === false) {
        cancelPending();
        stateRef.current = emptyState();
        publish();
        return;
      }

      if (headerIntent === true && resolveAllRows) {
        cancelPending();
        const snapshot = copyState(stateRef.current);
        headerSnapshotRef.current = snapshot;
        stateRef.current.model = incomingIds;
        resolve(
          'all',
          resolveAllRows,
          (rows) => {
            headerSnapshotRef.current = undefined;
            const state = stateRef.current;
            state.allMembers = new Set();
            for (const row of rows) {
              const id = idOf(row);
              state.allMembers.add(id);
              state.model.add(id);
              state.committed[String(id)] = row;
              const parentId = getParentRowId?.(row);
              if (parentId === undefined) continue;
              const members = state.members.get(parentId) ?? new Set<GridRowId>();
              members.add(id);
              state.members.set(parentId, members);
              state.model.add(parentId);
            }
          },
          () => {
            headerSnapshotRef.current = undefined;
            stateRef.current = snapshot;
          }
        );
        publish();
        return;
      }

      // A row change while a header walk is in flight cancels the walk: the change is applied to
      // the selection as it was BEFORE the header click, and the late response is ignored.
      const headerSnapshot = headerSnapshotRef.current;
      if (headerSnapshot && pendingRef.current.has('all')) {
        const optimistic = stateRef.current.model;
        const next = new Set(headerSnapshot.model);
        for (const id of optimistic) if (!incomingIds.has(id)) next.delete(id);
        for (const id of incomingIds) if (!optimistic.has(id)) next.add(id);
        pendingRef.current.delete('all');
        headerSnapshotRef.current = undefined;
        stateRef.current = copyState(headerSnapshot);
        incomingIds = next;
      }

      const state = stateRef.current;
      const previousIds = state.model;
      const nextIds = new Set(previousIds);

      for (const id of previousIds) {
        if (incomingIds.has(id)) continue;
        nextIds.delete(id);
        pendingRef.current.delete(id);
        const members = state.members.get(id);
        if (members) {
          for (const memberId of members) {
            nextIds.delete(memberId);
            state.manual.delete(memberId);
            delete state.committed[String(memberId)];
          }
          state.members.delete(id);
          continue;
        }
        state.manual.delete(id);
        state.allMembers.delete(id);
        delete state.committed[String(id)];
        for (const [parentId, parentMembers] of state.members) {
          if (parentMembers.has(id)) nextIds.delete(parentId);
        }
      }

      const api = apiRef?.current;
      for (const id of incomingIds) {
        if (previousIds.has(id)) continue;
        nextIds.add(id);
        const row = api?.getRow(id);
        if (!row) continue;

        if (isParentRow?.(row) && resolveChildRows) {
          resolve(
            id,
            () => resolveChildRows(row),
            (children) => {
              const current = stateRef.current;
              if (!current.model.has(id)) return;
              const members = new Set<GridRowId>();
              for (const child of children) {
                const childId = idOf(child);
                members.add(childId);
                current.model.add(childId);
                current.committed[String(childId)] = child;
              }
              current.members.set(id, members);
            },
            () => {
              stateRef.current.model.delete(id);
            }
          );
          continue;
        }

        state.manual.add(id);
        state.committed[String(id)] = row;
      }

      state.model = nextIds;
      publish();
    },
    [apiRef, cancelPending, getParentRowId, idOf, isParentRow, publish, resolve, resolveAllRows, resolveChildRows]
  );

  return {
    checkboxSelection: enableSelection,
    keepNonExistentRowsSelected: true,
    disableRowSelectionExcludeModel: true,
    rowSelectionModel: selectionModel,
    onRowSelectionModelChange,
  };
}
